import { Vec2 } from 'ayvri-std/lib/vector';
import { defaultValue } from './helpers';
import InputEventHandler, { EventType, KeyboardEventModifier } from './InputEventHandler';
import InputEventAggregator, { AggregatorType } from './InputEventAggregator';
import { DefaultKeyMap, DebugKeyMap, KeyboardActions } from './Keyboard';

/******************************************************************************
 * Constants
 *****************************************************************************/
const kInertiaMaxClickTimeThreshold = 2.0;
const kMaxRotationRatio = 0.1;
const kMaxZoomRatio = 0.1; // Limits zoom amount for massive inputs or slow frames
const kRotateInertiaDecay = 0.9;
const kIgnoreKeyElements = ['INPUT', 'TEXTAREA'];

const rotateEventType = AggregatorType.LEFT_DRAG;
const pitchRotateModifier = KeyboardEventModifier.CTRL;
const zoomEventTypes = [AggregatorType.RIGHT_DRAG, AggregatorType.WHEEL, AggregatorType.PINCH];

/******************************************************************************
 * Listen for pointer/touch events on the player and distill them down into
 * high level events for consumption by systems like Camera. The touch event
 * and aggregation systems are based on Cesium's.
 *****************************************************************************/
export class InputController {
  onPlayerStart(app) {
    window.addEventListener('keydown', keyHandler.bind(null, app), false);
  }

  onSceneLoad(ui, app) {
    this.inputHandler = new InputEventHandler(ui.globeCanvas, true);
    this.inputAggregator = new InputEventAggregator(ui.globeCanvas, this.inputHandler);

    // Register a left click listener
    this.inputHandler.setInputAction(() => app.onClick(), EventType.LEFT_CLICK);
  }

  update(state) {
    if (!this.inputAggregator) {
      return;
    }
    this._zoomMovement = updateZoom(this.inputAggregator, state.layout.height);
    this._rotateMovement = updateRotate(this.inputAggregator, state.layout.width, state.layout.height);

    this.inputAggregator.reset();
  }

  get rotateMotion() {
    return this._rotateMovement;
  }
  get isRotating() {
    return this.inputAggregator.isButtonDown(rotateEventType) || this.isModifyingPitch;
  }
  get isModifyingPitch() {
    return this.inputAggregator.isButtonDown(rotateEventType, pitchRotateModifier);
  }

  get zoomMotion() {
    return this._zoomMovement;
  }
  get isZooming() {
    const button = zoomEventTypes.some(e => this.inputAggregator.isButtonDown(e));
    return button || !!this._zoomMovement;
  }
}

/******************************************************************************
 * Helpers
 *****************************************************************************/
function keyHandler(app, event) {
  if (kIgnoreKeyElements.includes(event.target.nodeName)) return false;

  const keyMap = app.store.getState().input.keyMap;

  // See https://developer.mozilla.org/en-US/docs/Web/API/KeyboardEvent/code
  // for discussion on why event.code is used here
  const action = keyMap[event.code];
  if (action) {
    const func = KeyboardActions[action];
    func(app, event);
    event.preventDefault();
  }

  return true;
}

function updateZoom(input, canvasHeight) {
  // There won't be more than one input type moving at once, just pick one
  let movement = zoomEventTypes.reduce((move, idx) => (input.isMoving(idx) ? input.getMovement(idx) : move), null);

  if (!movement) {
    return false;
  }

  // Pinch events have some extra information we don't need
  let multiplier = 1.0;
  if (movement.distance) {
    movement = movement.distance;

    // Pinch events feel a bit weak, let's give them some more weight
    multiplier = 2.0;
  }

  // Normalize the zoom amount across the different input types
  // (e.g. mouse wheel vs right click drag)
  const diff = movement.endPosition.y - movement.startPosition.y;
  let rangeWindowRatio = (diff * multiplier) / canvasHeight;
  rangeWindowRatio = Math.min(rangeWindowRatio, kMaxZoomRatio);

  return rangeWindowRatio;
}

const rotationScratchStart = new Vec2();
const rotationScratchEnd = new Vec2();
function updateRotate(input, canvasWidth, canvasHeight) {
  // Allow rotation when the pitch modifier key is pressed.
  // @NOTE: InputEventHandler treats this as a separate button from the rotateEventType
  const modifier = input.isButtonDown(rotateEventType, pitchRotateModifier) ? pitchRotateModifier : undefined;

  let movement = input.isMoving(rotateEventType, modifier) && input.getMovement(rotateEventType, modifier);
  const lastMovement = input.getLastMovement(rotateEventType, modifier);

  if (!movement && lastMovement) {
    // Maintain intertia after input stops
    const ts = input.getButtonPressTime(rotateEventType, modifier);
    const tr = input.getButtonReleaseTime(rotateEventType, modifier);
    const threshold = ts && tr && (tr.getTime() - ts.getTime()) / 1000.0;
    const fromNow = tr && (Date.now() - tr.getTime()) / 1000.0;

    if (ts && tr && threshold < kInertiaMaxClickTimeThreshold) {
      const d = decay(fromNow, kRotateInertiaDecay);

      if (d <= 0.0001) {
        return false;
      }

      movement = {};
      const diff = Vec2.subtract(lastMovement.endPosition, lastMovement.startPosition, rotationScratchEnd);
      movement.startPosition = rotationScratchStart.copy(lastMovement.endPosition);
      movement.endPosition = Vec2.multiply(diff, d, diff);
      movement.endPosition = Vec2.add(movement.startPosition, movement.endPosition, movement.endPosition);
    }
  }

  if (!movement) {
    return false;
  }

  // Normalized delta in x and y
  let x = (movement.startPosition.x - movement.endPosition.x) / canvasWidth;
  let y = (movement.startPosition.y - movement.endPosition.y) / canvasHeight;
  x = Math.min(x, kMaxRotationRatio);
  y = Math.min(y, kMaxRotationRatio);

  return { x, y };
}

function decay(time, coefficient) {
  if (time < 0) {
    return 0.0;
  }

  const tau = (1.0 - coefficient) * 25.0;
  return Math.exp(-tau * time);
}

/******************************************************************************
 * Reducer
 *****************************************************************************/
export function inputReducer(state = { keyMap: DefaultKeyMap }, action) {
  switch (action.type) {
    case 'SET_VIEWER_OPTIONS': {
      const keyMapDict = {
        default: DefaultKeyMap,
        debug: DebugKeyMap,
      };

      const keyMap = defaultValue(keyMapDict[action.options.keyMap], DefaultKeyMap);
      return { ...state, keyMap };
    }
    default:
  }
  return state;
}

/******************************************************************************
 * Export
 *****************************************************************************/
export default InputController;
